import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import { brand } from "@/content/site";

const owner = brand.owner;
const abn = brand.abn;

const sections = [
  {
    h: "1. Introduction",
    p: [
      `Welcome to ${brand.name}. These Terms & Conditions ("Terms") govern your use of our website and services. By accessing or using our website, you agree to be bound by these Terms.`,
      `These Terms apply to ${owner} (ABN ${abn}) ("we", "us", "our"). References to "we", "us" or "our" throughout these Terms refer to ${owner}.`,
    ],
  },
  {
    h: "2. Our services",
    p: [
      "Green Circle Group provides subscription-based digital growth and business-development services for Australian trade and home-service businesses. Depending on the plan, this can include local visibility, reviews, content, advertising, landing pages, lead capture, follow-up workflows, CRM support and appointment-booking assistance.",
      "The specific inclusions, service areas, usage allowances and response scope for any engagement are confirmed in a written client proposal.",
    ],
  },
  {
    h: "3. No guaranteed outcomes",
    p: [
      "The information provided on our website is for general purposes only and does not constitute financial, legal or professional advice of any kind.",
      "Results vary according to the market, offer, competition, budget, capacity and client response. Green Circle Group does not guarantee rankings, lead volume, sales, revenue or booked jobs. We recommend that you seek independent professional advice before making commercial decisions.",
    ],
  },
  {
    h: "4. Fees and advertising spend",
    p: [
      "Subscription fees and any onboarding fees are provided following a Growth Assessment. Advertising media spend is separate from subscription fees unless expressly stated in a written proposal.",
    ],
  },
  {
    h: "5. User responsibilities",
    p: ["When using our website, you agree to:"],
    list: [
      "Provide accurate and truthful information",
      "Use the website for lawful purposes only",
      "Not attempt to interfere with the proper functioning of the website",
      "Not use automated systems to access the website without our permission",
      "Not misrepresent your identity or affiliation",
    ],
  },
  {
    h: "6. Client responsibilities",
    p: [
      "The client remains responsible for their business, brand, customer relationships and major commercial decisions, and for providing accurate information and approvals where required.",
      "Any database or reactivation communications are subject to consent, privacy, identification and unsubscribe requirements.",
    ],
  },
  {
    h: "7. Privacy",
    p: [
      `Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which forms part of these Terms.`,
      "By using our website and submitting your information, you consent to the collection, use and disclosure of your information as described in our Privacy Policy.",
    ],
  },
  {
    h: "8. Intellectual property",
    p: [
      `All content on our website, including text, graphics, logos, images and software, is the property of ${owner} or its licensors and is protected by Australian and international copyright laws.`,
      "You may not reproduce, distribute, modify or create derivative works from any content on our website without our prior written consent.",
    ],
  },
  {
    h: "9. Limitation of liability",
    p: [
      `To the maximum extent permitted by law, ${owner} shall not be liable for any indirect, incidental, special, consequential or punitive damages arising out of or related to your use of our website or services.`,
      "We do not guarantee the accuracy, completeness or reliability of any information on our website and shall not be liable for any decisions made based on such information.",
    ],
  },
  {
    h: "10. Third party links",
    p: [
      "Our website may contain links to third-party websites. We are not responsible for the content, privacy practices or terms of use of any third-party websites. Your use of such websites is at your own risk.",
    ],
  },
  {
    h: "11. Changes to these Terms",
    p: [
      "We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our website after any changes indicates your acceptance of the modified Terms.",
    ],
  },
  {
    h: "12. Governing law",
    p: [
      "These Terms are governed by and construed in accordance with the laws of Australia. Any disputes arising from these Terms or your use of our website shall be subject to the exclusive jurisdiction of the courts of Australia.",
    ],
  },
  {
    h: "13. Contact us",
    p: [
      "If you have any questions about these Terms, please contact us at:",
      `${owner}`,
      `ABN: ${abn}`,
      `Email: ${brand.email}`,
      `Website: ${brand.name}`,
    ],
  },
];

export default function Terms() {
  return (
    <>
      <SEO
        title="Terms & Conditions | Green Circle Group"
        description="Terms and conditions for Green Circle Group and Green Circle Group's subscription-based digital growth services."
        path="/terms"
      />
      <PageHero
        eyebrow="Legal"
        heading="Terms & Conditions"
        copy="Last updated: December 2025"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Terms", to: "/terms" },
        ]}
      />
      <section className="bg-white pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-10">
          {sections.map((s) => (
            <div key={s.h}>
              <h2 className="text-2xl font-extrabold text-forest">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="mt-3 text-mid-neutral leading-relaxed">
                  {para}
                </p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-mid-neutral">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-mint shrink-0" />
                      {i}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
